import {
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    LinearProgress,
    linearProgressClasses,
    Paper,
    styled
} from "@mui/material";
import Typography from "@mui/material/Typography";
import React, {useContext, useEffect, useRef, useState} from "react";
import Grid from "@mui/material/Grid2";
import Box from "@mui/material/Box";
import { EventSource } from 'extended-eventsource';
import {IsOnboardedContext} from "../../Contexts/IsOnboardedContext";
import Button from "@mui/material/Button";
import {Link} from "react-router-dom";
import {useTheme} from "@mui/material/styles";
import {useDispatch} from "react-redux";
import JobStatus from "../../Helpers/JobStatus";
import Moment from "moment";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReplayIcon from "@mui/icons-material/Replay";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ConfirmationDialog from "../Dialogs/ConfirmationDialog";
import {postReleaseJob, postResubmitJob} from "../../redux/ProjectsSlice";
import {useParams} from "react-router";
import LoadingWithStatusOverlay from "../Loading/LoadingWithStatusOverlay";
import TextField from "@mui/material/TextField";
import {alphaNumericEmptyValidator, alphaNumericValidator} from "../../Helpers/ValidationHelpers";

const ProjectJobStatusView = ({quote}) => {
    const {id} = useParams();
    const dispatch = useDispatch();
    const [progress, setProgress] = useState(0);
    const [completedSuccessfully, setCompletedSuccessfully] = useState(false);
    const [failed, setFailed] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState();
    const [releaseComment, setReleaseComment] = useState('');
    const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('');
    const [releaseCommentError, setReleaseCommentError] = useState(false);
    const [purchaseOrderNumberError, setPurchaseOrderNumberError] = useState(false);
    const [vaultLink, setVaultLink] = useState(quote.jobVaultLink);
    const [loadingStatus, setLoadingStatus] = useState("idle");
    const [loadingMessage, setLoadingMessage] = useState("");
    const theme = useTheme();

    const context = useContext(IsOnboardedContext);

    const progressIntervalRef = useRef(null); 
    const eventSourceRef = useRef(null);

    const updateQuoteInStore = (updatedData) => {
        const updatedQuote = {
            ...quote,
            jobStatus: updatedData.jobStatus,
            jobStatusInt: JobStatus.toInt(updatedData.jobStatus),
            jobStepNumber: updatedData.jobStepNumber,
            jobStatusMessage: updatedData.message || quote.jobStatusMessage,
            jobVaultLink: updatedData.jobVaultLink || quote.jobVaultLink
        };

        dispatch({
            type: 'quotes/quoteUpdatedFromStream',
            payload: updatedQuote
        });
    };

    const gradualProgressUpdate = (targetProgress) => {
        clearInterval(progressIntervalRef.current); 
        
        progressIntervalRef.current = setInterval(() => {
            setProgress((prevProgress) => {
                if (prevProgress >= targetProgress) {
                    clearInterval(progressIntervalRef.current);
                    return targetProgress;
                }
                return Math.min(prevProgress + 3, targetProgress);
            });
        }, 30); 
    };
    
    useEffect(() => {
        setStatus(quote.jobStatusInt);
        if (quote.jobStatus === "Design Representations Generated" || quote.jobStatus === "Job Completed") {
            setCompletedSuccessfully(true);
            setVaultLink(quote.jobVaultLink);
            setProgress(100);
        } else if (quote.jobStatus === "Failed") {
            setFailed(true);
            setProgress(100);
        } else {
            setProgress(quote.jobStepNumber / quote.jobProgressSteps * 100);
            eventSourceRef.current = new EventSource(`${process.env.REACT_APP_PRODUCT_API_BASEADDRESS}quotes/${quote.id}/jobstatus/stream`, {
                headers: {
                    Authorization: `Bearer ${context.accessToken}`
                },
                retry: 1000,
            });

            eventSourceRef.current.onopen = () => {
                clearInterval(progressIntervalRef.current);
            };

            eventSourceRef.current.onmessage = (event) => {
                const rawData = event.data;

                if (!rawData) {
                    console.warn("Received empty or undefined message");
                    return;
                }

                try {
                    const parsedData = JSON.parse(rawData);
                    if (parsedData.jobStatus !== 6 && parsedData.jobStatus !== 7 && parsedData.jobStatus !== 9) {
                        gradualProgressUpdate(parsedData.jobStepNumber / quote.jobProgressSteps * 100);
                    } else {
                        clearInterval(progressIntervalRef.current); // Stop any ongoing animation
                        setProgress(100);
                    }

                    setStatus(parsedData.jobStatus); // int
                    if (!!parsedData.jobVaultLink) {
                        setVaultLink(parsedData.jobVaultLink);
                    }
                    setMessage(parsedData.message);
                    updateQuoteInStore(parsedData);
                } catch (err) {
                    console.error("Error parsing JSON:", err);
                    console.error("Received data that caused error:", rawData);
                }
            };

            eventSourceRef.current.onerror = (error) => {
                console.error("Error occurred:", error);
            };
        }

        setMessage(quote.jobStatusMessage ?? "Processing...");

        return () => {
            clearInterval(progressIntervalRef.current);
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, [quote]);

    useEffect(() => {
        if (status === 7) {
            setFailed(true);
        } else if (status === 6 || status === 9) {
            setCompletedSuccessfully(true);
        }

        if (status === 7 || status === 6 || status === 9) {
            eventSourceRef?.current?.close();
        }
    }, [status])

    const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
        height: 15,
        borderRadius: 5,
        [`&.${linearProgressClasses.colorPrimary}`]: {
            backgroundColor: theme.palette.grey[200],
            ...theme.applyStyles('dark', {
                backgroundColor: theme.palette.grey[800],
            }),
        },
        [`& .${linearProgressClasses.bar}`]: {
            borderRadius: 5,
            backgroundColor: completedSuccessfully
                ? theme.palette.success.main
                : (failed ? theme.palette.error.main : theme.palette.primary.main),
            animation: progress < 100 && !completedSuccessfully && !failed
                ? 'loading 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                : 'none',
            '@keyframes loading': {
                '0%': {
                    backgroundPosition: '0% 50%',
                    opacity: 1,
                },
                '50%': {
                    backgroundPosition: '100% 50%',
                    opacity: 0.8,
                },
                '100%': {
                    backgroundPosition: '0% 50%',
                    opacity: 1,
                },
            },
            backgroundSize: '200% 200%',
            backgroundImage: progress < 100 && !completedSuccessfully && !failed
                ? `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light || theme.palette.primary.secondary} 100%)`
                : 'none',
            ...theme.applyStyles('dark', {
                backgroundColor: '#308fe8',
            })
        },
        [`&.${linearProgressClasses.indeterminate}`]: {
            [`& .${linearProgressClasses.bar1}`]: {
                animation: progress < 100 && !completedSuccessfully && !failed
                    ? 'loading 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                    : 'mui-indeterminate1 2.1s cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite',
                backgroundImage: progress < 100 && !completedSuccessfully && !failed
                    ? `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light || theme.palette.primary.secondary} 100%)`
                    : 'none',
                backgroundSize: '200% 200%',
            },
            [`& .${linearProgressClasses.bar2}`]: {
                animation: progress < 100 && !completedSuccessfully && !failed
                    ? 'loading 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                    : 'mui-indeterminate2 2.1s cubic-bezier(0.165, 0.84, 0.44, 1) infinite',
                animationDelay: progress < 100 && !completedSuccessfully && !failed ? '0.75s' : '1.15s',
                backgroundImage: progress < 100 && !completedSuccessfully && !failed
                    ? `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light || theme.palette.primary.secondary} 100%)`
                    : 'none',
                backgroundSize: '200% 200%',
            },
        },
        '@keyframes loading': {
            '0%': {
                backgroundPosition: '0% 50%',
                opacity: 1,
            },
            '50%': {
                backgroundPosition: '100% 50%',
                opacity: 0.8,
            },
            '100%': {
                backgroundPosition: '0% 50%',
                opacity: 1,
            },
        },
        '@keyframes mui-indeterminate1': {
            '0%': {
                left: '-35%',
                right: '100%',
            },
            '60%': {
                left: '100%',
                right: '-90%',
            },
            '100%': {
                left: '100%',
                right: '-90%',
            },
        },
        '@keyframes mui-indeterminate2': {
            '0%': {
                left: '-200%',
                right: '100%',
            },
            '60%': {
                left: '107%',
                right: '-8%',
            },
            '100%': {
                left: '107%',
                right: '-8%',
            },
        },
    }));

    const handleConfirmRelease = () => {
        const commentErr = releaseComment ? alphaNumericEmptyValidator(releaseComment) : false;
        const poErr = !purchaseOrderNumber ? "Purchase Order Number is required." : alphaNumericValidator(purchaseOrderNumber);
        setReleaseCommentError(commentErr);
        setPurchaseOrderNumberError(poErr);
        if (commentErr || poErr) return false;

        setLoadingStatus("loading");
        setLoadingMessage("Releasing...");
        dispatch(postReleaseJob({
            id,
            payload: {
                releaseComment: releaseComment?.trim() || null,
                purchaseOrderNumber: purchaseOrderNumber?.trim() || null,
            }
        }))
            .unwrap()
            .then(() => {
                setLoadingStatus("success");
                setLoadingMessage(`Success!`);
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            })
            .catch((error) => {
                console.error('Failed to release job', error);
                setLoadingStatus("error");
                setLoadingMessage('Failed to release job');
            });
    };

    const isRevision0A = quote.majorRevision === 0 && (quote.minorRevision === 'A' || quote.minorRevision === null || quote.minorRevision === undefined);
    const [resubmitDialogOpen, setResubmitDialogOpen] = useState(false);

    const handleResubmit = (isHardResubmission) => {
        setResubmitDialogOpen(false);
        setLoadingStatus("loading");
        setLoadingMessage("Resubmitting...");

        const payload = {
            majorRevision: quote.majorRevision,
            minorRevision: quote.minorRevision,
            isHardResubmission
        };

        dispatch(postResubmitJob({ id, payload }))
            .unwrap()
            .then(() => {
                setLoadingStatus("success");
                setLoadingMessage("Resubmitted successfully!");
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            })
            .catch((error) => {
                console.error('Failed to resubmit job', error);
                setLoadingStatus("error");
                setLoadingMessage('Failed to resubmit job');
            });
    };

    return (
        <Paper  sx={{
            borderRadius: '4px !important',
            overflow: 'hidden',
        }}>
            <Grid container spacing={0}>
                <Grid size={{ xs: 12 }}>
                    <Typography variant="h6" sx={{ backgroundColor: theme.palette.tertiary.secondary, color: 'white', p: 1 }}>Job</Typography>
                </Grid>
                <Grid size={{ xs: 12}}>
                    <Box
                        sx={{
                            display:'flex',
                            justifyContent: 'space-between',
                            padding: '10px',
                            borderBottom: `1px solid ${theme.palette.divider}`
                        }}
                    >
                        <Typography sx={{ fontWeight: 'bold'}}>Current Revision</Typography>
                        <Typography>
                            {quote.minorRevision == null
                                ? `${quote.majorRevision}`
                                : `${quote.majorRevision}.${quote.minorRevision}`}
                        </Typography>
                    </Box>
                </Grid>
                {
                    quote.jobConvertedBy &&
                    <Grid size={{ xs: 12}}>
                        <Box
                            sx={{
                                display:'flex',
                                justifyContent: 'space-between',
                                padding: '10px',
                                borderBottom: `1px solid ${theme.palette.divider}`
                            }}
                        >
                            <Typography sx={{ fontWeight: 'bold'}}>Converted to Job by</Typography>
                            <Typography>{quote.jobConvertedBy}</Typography>
                        </Box>
                    </Grid>
                }
                {
                    quote.jobConvertedAt &&
                    <Grid size={{ xs: 12}}>
                        <Box
                            sx={{
                                display:'flex',
                                justifyContent: 'space-between',
                                padding: '10px',
                                borderBottom: `1px solid ${theme.palette.divider}`
                            }}
                        >
                            <Typography sx={{ fontWeight: 'bold'}}>Converted At</Typography>
                            <Typography>{Moment(quote.jobConvertedAt).format('D MMM YYYY, h:mma')}</Typography>
                        </Box>
                    </Grid>
                }
                {
                    <Grid size={{ xs: 12 }}>
                        <Box sx={{ m: 3 }}>
                            <BorderLinearProgress
                                variant={
                                    (status === 8 || quote.jobStatus === "Job in Progress") && !failed && !completedSuccessfully
                                        ? "indeterminate"
                                        : "determinate"
                                }
                                value={
                                    (status === 8 || quote.jobStatus === "Job in Progress") && !failed && !completedSuccessfully
                                        ? undefined
                                        : progress
                                }
                            />
                            <Typography variant="subtitle2" align="center" sx={{ marginTop: 1 }}>
                                {message}
                            </Typography>
                        </Box>
                    </Grid>
                }
                <Grid size={{ xs: 12 }}  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 2 }}>
                    {vaultLink != null && (
                        <Collapse in={true}>
                            <Link target="_blank" rel="noreferrer" to={vaultLink}>
                                <Button variant="contained" color="tertiary" startIcon={<OpenInNewIcon />} sx={{
                                    color: 'white',
                                }}>
                                    View Files
                                </Button>
                            </Link>
                        </Collapse>
                    )}
                    {completedSuccessfully && quote.isReleased === false && (
                        <ConfirmationDialog
                            title="Release this job?"
                            description="By releasing this job, you acknowledge that the generated general assembly (GA) has been verified and approved. This action will generate the full drawing pack and incur a charge."
                            confirmButtonText="Release"
                            cancelButtonText="Cancel"
                            response={handleConfirmRelease}
                            header="Confirm Release"
                            onClose={() => {
                                setReleaseComment('');
                                setPurchaseOrderNumber('');
                                setReleaseCommentError(false);
                                setPurchaseOrderNumberError(false);
                            }}
                            content={
                            <>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Purchase Order Number"
                                    required
                                    value={purchaseOrderNumber}
                                    onChange={(e) => {
                                        setPurchaseOrderNumber(e.target.value);
                                        setPurchaseOrderNumberError(
                                            !e.target.value
                                                ? "Purchase Order Number is required."
                                                : alphaNumericValidator(e.target.value)
                                        );
                                    }}
                                    error={!!purchaseOrderNumberError}
                                    helperText={purchaseOrderNumberError || ''}
                                    inputProps={{ maxLength: 255 }}
                                    placeholder=""
                                />
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Release comment (optional)"
                                    value={releaseComment}
                                    onChange={(e) => {
                                        setReleaseComment(e.target.value);
                                        setReleaseCommentError(e.target.value ? alphaNumericEmptyValidator(e.target.value) : false);
                                    }}
                                    error={!!releaseCommentError}
                                    helperText={releaseCommentError || ''}
                                    inputProps={{ maxLength: 255 }}
                                    placeholder=""
                                />
                            </>
                            }
                        >
                            {(showDialog) =>
                                <Button variant="contained" color="success" onClick={showDialog} startIcon={<CheckCircleIcon />} sx={{ ml: 2 }}>
                                    Release
                                </Button>}
                        </ConfirmationDialog>
                    )}
                    {(completedSuccessfully || failed) && !quote.isReleased && (
                        <>
                            <Button
                                variant="contained"
                                color="warning"
                                onClick={() => setResubmitDialogOpen(true)}
                                startIcon={<ReplayIcon />}
                                sx={{ ml: 2 }}
                            >
                                Resubmit
                            </Button>
                            <Dialog
                                open={resubmitDialogOpen}
                                onClose={() => setResubmitDialogOpen(false)}
                                maxWidth="sm"
                                fullWidth
                            >
                                <Box
                                    sx={{
                                        backgroundColor: (theme) => theme.palette.primary.main,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        px: 2,
                                        py: 1,
                                    }}
                                >
                                    <Typography variant="h6" sx={{ color: (theme) => theme.palette.primary.contrastText }}>
                                        Confirm Resubmission
                                    </Typography>
                                    <IconButton
                                        aria-label="close"
                                        onClick={() => setResubmitDialogOpen(false)}
                                        sx={{ color: (theme) => theme.palette.primary.contrastText }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </Box>
                                <DialogTitle>Resubmit this job?</DialogTitle>
                                <DialogContent>
                                    {isRevision0A ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                                            <DialogContentText>
                                                This is a fresh project creation (revision 0A). Choose how you would like to resubmit:
                                            </DialogContentText>
                                            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                                                <Paper
                                                    variant="outlined"
                                                    sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}
                                                >
                                                    <Typography variant="subtitle1" fontWeight="bold">Full Resubmit</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Completely redoes the copy design process from scratch.
                                                    </Typography>
                                                    <Button
                                                        onClick={() => handleResubmit(true)}
                                                        color="primary"
                                                        variant="contained"
                                                        sx={{ mt: 'auto' }}
                                                    >
                                                        Full Resubmit
                                                    </Button>
                                                </Paper>
                                                <Paper
                                                    variant="outlined"
                                                    sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}
                                                >
                                                    <Typography variant="subtitle1" fontWeight="bold">Re-apply Parameters</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Reapplies parameters and reruns logic on the existing models.
                                                    </Typography>
                                                    <Button
                                                        onClick={() => handleResubmit(false)}
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{ mt: 'auto' }}
                                                    >
                                                        Re-apply Parameters
                                                    </Button>
                                                </Paper>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <DialogContentText>
                                            Are you sure you want to resubmit this job?
                                        </DialogContentText>
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    {!isRevision0A && (
                                        <Button onClick={() => handleResubmit(false)} color="primary" variant="contained">
                                            Confirm
                                        </Button>
                                    )}
                                    <Button onClick={() => setResubmitDialogOpen(false)} color="primary" variant="outlined">
                                        Cancel
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </>
                    )}
                </Grid>
            </Grid>
            <LoadingWithStatusOverlay
                status={loadingStatus}
                message={loadingMessage}
                successTimeout={1500}
                failureTimeout={5000}
                onComplete={() => {
                    if (loadingStatus === "success") {

                    }
                    setLoadingStatus("idle");
                    setLoadingMessage("");
                }}
            />
        </Paper>
    )
}
export default ProjectJobStatusView;